// Summary cards for the filtered set: how much Merlin is talking, where, at
// what cost, and how people reacted. Every number is the backend's roll-up of
// exactly the rows the table can page through.

import type { MonitorFacets, MonitorTotals } from '../../services/merlinMonitor';
import { formatMoney } from '../../utils/formatting';
import { streamTone } from './monitorDisplay';
import styles from './MonitorSummaryCards.module.css';

export interface MonitorSummaryCardsProps {
  totals: MonitorTotals | null;
  facets: MonitorFacets | null;
  loading: boolean;
}

function n(v: number | undefined): string {
  return (v ?? 0).toLocaleString();
}

export function MonitorSummaryCards({ totals, facets, loading }: MonitorSummaryCardsProps) {
  const streamTotal = (facets?.streams ?? []).reduce((acc, f) => acc + f.count, 0);
  return (
    <div className={`${styles.cards} ${loading ? styles.loading : ''}`} data-testid="monitor-summary">
      <div className={styles.card}>
        <p className={styles.head}>Conversations</p>
        <p className={styles.big} data-testid="total-conversations">
          {n(totals?.conversations)}
        </p>
        <p className={styles.sub}>
          <span className={styles.live}>{n(totals?.running)} running</span>
          {' · '}
          {n(totals?.failed)} failed · {n(totals?.archived)} archived
        </p>
        <p className={styles.sub}>
          {n(totals?.distinct_owners)} {totals?.distinct_owners === 1 ? 'person' : 'people'} ·{' '}
          {n(totals?.with_channels)} over email / SMS
        </p>
      </div>

      <div className={styles.card}>
        <p className={styles.head}>By stream</p>
        {streamTotal > 0 ? (
          <>
            <div className={styles.splitbar} aria-hidden="true">
              {(facets?.streams ?? []).map((f) => (
                <i
                  key={f.value}
                  style={{
                    width: `${(f.count / streamTotal) * 100}%`,
                    background: streamTone(f.value).ink,
                  }}
                  title={`${f.label}: ${f.count}`}
                />
              ))}
            </div>
            <ul className={styles.legend}>
              {(facets?.streams ?? []).map((f) => (
                <li key={f.value}>
                  <span
                    className={styles.dot}
                    style={{ background: streamTone(f.value).ink }}
                  />
                  {f.label} <strong>{f.count.toLocaleString()}</strong>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className={styles.sub}>Nothing in this view yet.</p>
        )}
      </div>

      <div className={styles.card}>
        <p className={styles.head}>Volume</p>
        <p className={styles.big}>{n(totals?.assistant_turns)}</p>
        <p className={styles.sub}>Merlin replies · {n(totals?.messages)} messages total</p>
        <p className={styles.sub}>{n(totals?.tool_calls)} tool calls</p>
      </div>

      <div className={styles.card}>
        <p className={styles.head}>Spend</p>
        <p className={styles.big} data-testid="total-cost">
          {formatMoney(totals?.cost_usd ?? '0')}
        </p>
        <p className={styles.sub}>
          {n(totals?.input_tokens)} in · {n(totals?.output_tokens)} out tokens
        </p>
        <p className={styles.sub}>
          <span className={styles.up}>👍 {n(totals?.feedback_up)}</span>
          {' · '}
          <span className={styles.down}>👎 {n(totals?.feedback_down)}</span>
        </p>
      </div>
    </div>
  );
}
